// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry, CellValueChangedEvent, themeQuartz } from 'ag-grid-community';
import { Box, Typography } from '@mui/material';
import { TimesheetEntry } from '../../types';
import MultiLineEditor from './MultiLineEditor';
import TimePickerEditor from './TimePickerEditor';
import {
  getWeekNumber,
  isFutureDate,
  validateEntryChange,
  SoftWarning,
  PendingChange,
} from '../../utils/timesheetValidation';
import { parseLocalDate } from '../../utils/dateUtils';

// Register AG-Grid modules (v35 requirement)
ModuleRegistry.registerModules([AllCommunityModule]);

// Create theme matching Google Sheets style
const myTheme = themeQuartz.withParams({
  borderRadius: 0,
  headerFontSize: 13,
  headerFontWeight: 700,
  fontSize: 13,
  rowBorder: { color: '#e0e0e0', width: 1 },
  columnBorder: { color: '#e0e0e0', width: 1 },
  headerColumnBorder: { color: '#e0e0e0', width: 1 },
  headerRowBorder: { color: '#bdbdbd', width: 1 },
  wrapperBorderRadius: 0,
});

interface TimesheetGridProps {
  entries: TimesheetEntry[];
  editable: boolean;
  expectedDailyHours: number;
  onEntryUpdate: (entryId: string, field: string, value: any) => void;
  onValidationWarning?: (warnings: SoftWarning[], pendingChange: PendingChange) => void;
  onValidationError?: (message: string) => void;
  onFutureMonthClick?: () => void;
  gridHeight?: string;
}

function isWeekend(dateStr: string): boolean {
  const day = parseLocalDate(dateStr).getDay();
  return day === 0 || day === 6;
}

function formatDate(dateStr: string): string {
  const d = parseLocalDate(dateStr);
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${days[d.getDay()]}, ${d.getDate()} ${months[d.getMonth()]}`;
}

// Custom header component with small non-bold subtext
function SubtextHeaderRenderer(props: any) {
  const { main, sub } = props;
  return React.createElement('div', { style: { textAlign: 'center', lineHeight: '1.3' } },
    React.createElement('div', { style: { fontWeight: 700, fontSize: '13px' } }, main),
    React.createElement('div', { style: { fontWeight: 400, fontSize: '10px', color: '#666' } }, sub),
  );
}

// Cell renderer that converts **text** markers to bold HTML
function TaskDetailsCellRenderer(params: any) {
  const val = params.value;
  if (!val) return null;
  // Split by **...** pattern, alternating between normal and bold segments
  const parts = String(val).split(/\*\*(.*?)\*\*/g);
  // parts: [normal, bold, normal, bold, ...] — odd indices are bold
  const html = parts.map((part, i) => {
    // Escape HTML entities
    const escaped = part.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return i % 2 === 1 ? `<strong>${escaped}</strong>` : escaped;
  }).join('');

  return React.createElement('span', {
    dangerouslySetInnerHTML: { __html: html },
    style: { whiteSpace: 'pre-wrap' },
  });
}

export default function TimesheetGrid({ entries, editable, expectedDailyHours, onEntryUpdate, onValidationWarning, onValidationError, onFutureMonthClick, gridHeight }: TimesheetGridProps) {
  // Guard ref to prevent infinite loops when reverting cell values
  const isRevertingRef = useRef(false);
  const gridRef = useRef<any>(null);

  const rowData = useMemo(() => {
    const rows: any[] = [];
    let currentWeek: number | null = null;
    let weekTotalHours = 0;
    let orphanTotalHours = 0; // eslint-disable-line @typescript-eslint/no-unused-vars
    let hasOrphans = false; // eslint-disable-line @typescript-eslint/no-unused-vars

    entries.forEach((entry, idx) => {
      const weekNum = getWeekNumber(entry.entry_date);
      const isOrphan = weekNum === 0;
      const weekLabel = isOrphan ? '' : `Week ${String(weekNum).padStart(2, '0')}`;

      // If we moved to a new week/group, insert summary row for the previous one
      if (currentWeek !== null && weekNum !== currentWeek) {
        if (currentWeek > 0) {
          rows.push({
            id: `week-summary-${currentWeek}`,
            is_week_summary: true,
            week: '',
            date_display: `Week ${String(currentWeek).padStart(2, '0')} Total`,
            total_hours: weekTotalHours,
          });
        }
        weekTotalHours = 0;
      }

      if (isOrphan) {
        hasOrphans = true;
        orphanTotalHours += Number(entry.total_hours || 0);
      } else {
        weekTotalHours += Number(entry.total_hours || 0);
      }

      currentWeek = weekNum;

      rows.push({
        ...entry,
        week: weekLabel,
        date_display: formatDate(entry.entry_date),
        is_weekend: isWeekend(entry.entry_date),
        is_today: parseLocalDate(entry.entry_date).toDateString() === new Date().toDateString(),
        is_future: isFutureDate(entry.entry_date),
      });

      // If last entry, insert final week summary
      if (idx === entries.length - 1 && weekNum > 0) {
        rows.push({
          id: `week-summary-${weekNum}`,
          is_week_summary: true,
          week: '',
          date_display: `Week ${String(weekNum).padStart(2, '0')} Total`,
          total_hours: weekTotalHours,
        });
      }
    });

    return rows;
  }, [entries]);

  const defaultColDef: any = useMemo(() => ({
    sortable: false,
    filter: false,
    resizable: true,
    suppressMovable: true,
  }), []);

  const columnDefs: any[] = useMemo(() => [
    {
      field: 'week',
      headerName: 'Week',
      width: 100,
      pinned: 'left' as const,
      cellStyle: (params: any) => {
        if (params.data.is_week_summary) {
          return { fontWeight: 700, backgroundColor: '#e8f5e9', textAlign: 'center' };
        }
        return { fontWeight: 600, backgroundColor: '#f5f5f5', textAlign: 'center' };
      },
      editable: false,
    },
    {
      field: 'date_display',
      headerName: 'Date',
      width: 140,
      pinned: 'left' as const,
      editable: false,
      cellStyle: (params: any) => {
        if (params.data.is_week_summary) {
          return { fontWeight: 700, backgroundColor: '#e8f5e9', color: '#2e7d32' };
        }
        const styles: any = { fontWeight: 500 };
        if (params.data.is_future) {
          styles.color = '#bdbdbd';
          styles.backgroundColor = '#f5f5f5';
        } else if (params.data.is_weekend) {
          styles.color = '#d32f2f';
          styles.backgroundColor = '#fce4ec';
        }
        if (params.data.is_today) {
          styles.backgroundColor = '#bbdefb';
          styles.fontWeight = 700;
        }
        return styles;
      },
    },
    {
      field: 'start_time',
      headerName: 'Start Time (IST)',
      width: 155,
      editable: (params: any) => editable && !params.data.is_week_summary && !params.data.is_future,
      cellEditor: TimePickerEditor,
      cellEditorPopup: true,
      cellStyle: (params: any) => {
        if (params.data.is_week_summary) return { backgroundColor: '#e8f5e9' };
        if (params.data.is_future) return { textAlign: 'center', color: '#bdbdbd', backgroundColor: '#f5f5f5' };
        return { textAlign: 'center' };
      },
      valueFormatter: (params: any) => {
        if (params.data.is_week_summary) return '';
        return params.value ? String(params.value).substring(0, 5) : '';
      },
    },
    {
      field: 'finish_time',
      headerName: 'Finish Time (IST)',
      width: 155,
      editable: (params: any) => editable && !params.data.is_week_summary && !params.data.is_future,
      cellEditor: TimePickerEditor,
      cellEditorPopup: true,
      cellStyle: (params: any) => {
        if (params.data.is_week_summary) return { backgroundColor: '#e8f5e9' };
        if (params.data.is_future) return { textAlign: 'center', color: '#bdbdbd', backgroundColor: '#f5f5f5' };
        return { textAlign: 'center' };
      },
      valueFormatter: (params: any) => {
        if (params.data.is_week_summary) return '';
        return params.value ? String(params.value).substring(0, 5) : '';
      },
    },
    {
      field: 'task_details',
      headerName: 'Task Details',
      headerTooltip: 'Click to edit. Enter = new line, Tab = save.',
      flex: 1,
      minWidth: 300,
      editable: (params: any) => editable && !params.data.is_week_summary && !params.data.is_future,
      cellEditor: MultiLineEditor,
      cellEditorPopup: true,
      cellRenderer: TaskDetailsCellRenderer,
      autoHeight: true,
      wrapText: true,
      cellStyle: (params: any) => {
        if (params.data.is_week_summary) return { backgroundColor: '#e8f5e9' };
        if (params.data.is_future) return { whiteSpace: 'pre-wrap', lineHeight: '1.4', paddingTop: '8px', paddingBottom: '8px', color: '#bdbdbd', backgroundColor: '#f5f5f5' };
        return { whiteSpace: 'pre-wrap', lineHeight: '1.4', paddingTop: '8px', paddingBottom: '8px' };
      },
      suppressKeyboardEvent: (params: any) => {
        if (params.editing && params.event.key === 'Enter') return true;
        if (params.editing && params.event.key === 'Tab') return true;
        return false;
      },
    },
    {
      field: 'break_minutes',
      headerComponent: SubtextHeaderRenderer,
      headerComponentParams: { main: 'Break', sub: '(Non-Billable)' },
      width: 120,
      editable: (params: any) => editable && !params.data.is_week_summary && !params.data.is_future,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: ['1:00 hr', '30 min', '15 min', '0:00'],
      },
      valueGetter: (params: any) => {
        if (params.data?.is_week_summary) return '';
        const v = Number(params.data?.break_minutes ?? 0);
        if (v === 60) return '1:00 hr';
        if (v === 30) return '30 min';
        if (v === 15) return '15 min';
        if (v === 0) return '0:00';
        return '0:00';
      },
      valueSetter: (params: any) => {
        const label = params.newValue;
        let mins = 60;
        if (label === '30 min') mins = 30;
        else if (label === '15 min') mins = 15;
        else if (label === '0:00') mins = 0;
        params.data.break_minutes = mins;
        return true;
      },
      cellStyle: (params: any) => {
        if (params.data?.is_week_summary) return { backgroundColor: '#e8f5e9' };
        if (params.data?.is_future) return { textAlign: 'center', color: '#bdbdbd', backgroundColor: '#f5f5f5' };
        return { textAlign: 'center' };
      },
    },
    {
      field: 'total_hours',
      headerComponent: SubtextHeaderRenderer,
      headerComponentParams: { main: 'Total Hours', sub: '(Billable)' },
      width: 130,
      editable: false,
      cellStyle: (params: any) => {
        if (params.data.is_week_summary) {
          return { fontWeight: 700, textAlign: 'center', backgroundColor: '#e8f5e9', color: '#2e7d32', fontSize: '14px' };
        }
        if (params.data.is_future) {
          return { fontWeight: 600, textAlign: 'center', color: '#bdbdbd', backgroundColor: '#f5f5f5' };
        }
        return {
          fontWeight: 600,
          textAlign: 'center',
          color: Number(params.value) > 0 ? '#212121' : '#bdbdbd',
        };
      },
      valueFormatter: (params: any) => {
        const decimal = Number(params.value || 0);
        const hours = Math.floor(decimal);
        const mins = Math.round((decimal - hours) * 60);
        return `${hours}:${String(mins).padStart(2, '0')}`;
      },
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [editable]);

  const onCellValueChanged = useCallback((event: CellValueChangedEvent) => {
    // Skip validation when we're programmatically reverting a cell
    if (isRevertingRef.current) {
      isRevertingRef.current = false;
      return;
    }

    const { data, colDef, oldValue, newValue, node } = event;
    if (!colDef.field) return;

    // Build the field/value for the API call
    let field: string;
    let value: any;
    if (colDef.field === 'start_time' || colDef.field === 'finish_time') {
      field = '_time_update';
      value = {
        start_time: data.start_time || null,
        finish_time: data.finish_time || null,
      };
      value[colDef.field] = newValue || null;
    } else if (colDef.field === 'break_minutes') {
      // valueSetter already converted label to number on data.break_minutes
      field = 'break_minutes';
      value = data.break_minutes;
    } else {
      field = colDef.field;
      value = newValue;
    }

    // Run validation
    const result = validateEntryChange(data, field, value, entries);

    if (result.errors.length > 0 && onValidationError) {
      // HARD BLOCK: revert cell and show error
      isRevertingRef.current = true;
      node.setDataValue(colDef.field!, oldValue);
      onValidationError(result.errors[0].message);
      return;
    }

    if (result.warnings.length > 0 && onValidationWarning) {
      // SOFT WARNING: revert temporarily, show confirmation dialog
      isRevertingRef.current = true;
      node.setDataValue(colDef.field!, oldValue);
      onValidationWarning(result.warnings, {
        entryId: data.id,
        field,
        value,
        oldValue,
        rowNode: node,
        colField: colDef.field!,
      });
      return;
    }

    // No issues: proceed with save
    onEntryUpdate(data.id, field, value);
  }, [onEntryUpdate, onValidationWarning, onValidationError, entries]);

  // When grid is non-editable (future month), show warning on cell click
  const onCellClicked = useCallback((event: any) => {
    if (!editable && onFutureMonthClick) {
      const col = event.colDef?.field;
      if (col === 'start_time' || col === 'finish_time' || col === 'task_details' || col === 'break_minutes') {
        onFutureMonthClick();
      }
    }
  }, [editable, onFutureMonthClick]);

  const getRowId = useCallback((params: any) => {
    return params.data.id;
  }, []);

  // Save and restore scroll position when rowData changes (prevents scroll-to-top)
  const scrollTopRef = useRef<number>(0);

  // Before rowData changes, save the current scroll position
  useEffect(() => {
    const api = gridRef.current?.api;
    if (api) {
      // Save current scroll position before the re-render
      const viewport = document.querySelector('.ag-body-viewport');
      if (viewport) {
        scrollTopRef.current = viewport.scrollTop;
      }
    }
  }); // runs every render to capture scroll before rowData update

  // After rowData changes, restore scroll position
  useEffect(() => {
    if (scrollTopRef.current > 0) {
      setTimeout(() => {
        const viewport = document.querySelector('.ag-body-viewport');
        if (viewport) {
          viewport.scrollTop = scrollTopRef.current;
        }
      }, 50);
    }
  }, [rowData]);

  const getRowStyle = useCallback((params: any) => {
    if (params.data?.is_week_summary) {
      return { backgroundColor: '#e8f5e9', fontWeight: 700, fontSize: '14px', borderTop: '2px solid #333', borderBottom: '2px solid #333' };
    }
    if (params.data?.is_future) {
      return { backgroundColor: '#f5f5f5' };
    }
    if (params.data?.is_weekend) {
      return { backgroundColor: '#fff8f8' };
    }
    return undefined;
  }, []);

  return (
    <Box>
      <div style={{ width: '100%', height: gridHeight || 'calc(100vh - 240px)' }}>
        <AgGridReact
          ref={gridRef}
          theme={myTheme}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          getRowId={getRowId}
          onCellValueChanged={onCellValueChanged}
          onCellClicked={onCellClicked}
          getRowStyle={getRowStyle}
          singleClickEdit
          stopEditingWhenCellsLoseFocus
          headerHeight={44}
          rowHeight={40}
          tooltipShowDelay={500}
        />
      </div>
      {editable && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          Tip: Click any cell to edit. For Task Details — Enter = new line, Tab = save, Esc = cancel.
        </Typography>
      )}
    </Box>
  );
}
